import { Component, OnInit, inject, DestroyRef, ViewChild, signal, computed, WritableSignal } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of, switchMap, catchError, Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ImportService } from '../../../services/import/import.service';
import { TournamentService } from '../../../services/tournament/tournament.service';
import { PlayersService } from '../../../api-client/api/players.service';
import { LoggingService } from '../../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { ConfigService } from '../../../services/config/config.service';
import { AlertContainerComponent } from '../../shared/alert-container/alert-container.component';
import { ImportModalComponent, ImportOptions } from '../../import-modal/import-modal.component';
import { XmlImportModalComponent, XmlImportOptions } from '../xml-import-modal/xml-import-modal.component';
import { PlayerModalComponent, PlayerFormData } from '../player-modal/player-modal.component';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { SortableHeaderComponent } from '../../shared/sortable-header/sortable-header.component';
import { TablePaginationComponent, PageChangeEvent } from '../../shared/table-pagination/table-pagination.component';
import { SortConfig, TableSortingService } from '../../../services';
import { TablePaginationService, PaginationResult } from '../../../services/shared/table-pagination/table-pagination.service';
import { TournamentPlayer } from '../../../models/tournamentPlayer';
import { Tournament } from '../../../models/tournament';
import { ModulesImportsEndpointsPlayersImportPlayersFromXmlResponse } from '../../../api-client/model/modulesImportsEndpointsPlayersImportPlayersFromXmlResponse';
import { ModulesPlayersEndpointsContractsPlayersCreatePlayerRequest } from '../../../api-client/model/modulesPlayersEndpointsContractsPlayersCreatePlayerRequest';
import { ModulesPlayersEndpointsContractsPlayersUpdatePlayerRequestModel } from '../../../api-client/model/modulesPlayersEndpointsContractsPlayersUpdatePlayerRequestModel';
import { ModulesPlayersApplicationPlayerTypesPlayerTypeResponse } from '../../../api-client/model/modulesPlayersApplicationPlayerTypesPlayerTypeResponse';
import { FieldParametersModalComponent, FieldParameterValues } from '../field-parameters-modal/field-parameters-modal.component';
import { PlayerTypeLinkService } from '../../../services/player/player-type-link.service';
import { DialogService } from '../../../services/shared/dialog/dialog.service';
import { ReportExecutionService } from '../../../services/reports/report-execution.service';

interface XmlPlayerImportFailure {
    playerName?: string;
    wwgrId?: number;
    errorCode?: string;
    errorMessage?: string;
}

type XmlImportResponse = ModulesImportsEndpointsPlayersImportPlayersFromXmlResponse & {
    totalPlayerCount?: number;
    failedCount?: number;
    failures?: XmlPlayerImportFailure[];
};

@Component({
    selector: 'app-tournament-players',
    standalone: true,
    imports: [AlertContainerComponent, ImportModalComponent, XmlImportModalComponent, PlayerModalComponent, ConfirmationDialogComponent, SortableHeaderComponent, TablePaginationComponent, FieldParametersModalComponent],
    providers: [PlayersService],
    templateUrl: './tournament-players.component.html',
    styleUrls: ['./tournament-players.component.scss']
})
export class TournamentPlayersComponent implements OnInit {
    @ViewChild(ImportModalComponent) importModal!: ImportModalComponent;
    @ViewChild(XmlImportModalComponent) xmlImportModal!: XmlImportModalComponent;
    @ViewChild(PlayerModalComponent) playerModal!: PlayerModalComponent;
    @ViewChild(ConfirmationDialogComponent) confirmationDialog!: ConfirmationDialogComponent;
    @ViewChild(FieldParametersModalComponent) fieldParametersModal!: FieldParametersModalComponent;

    private route = inject(ActivatedRoute);
    private destroyRef = inject(DestroyRef);
    private importService = inject(ImportService);
    private tournamentService = inject(TournamentService);
    private playersService = inject(PlayersService);
    private loggingService = inject(LoggingService);
    private toastr = inject(ToastrService);
    private configService = inject(ConfigService);
    private tableSortingService = inject(TableSortingService);
    private tablePaginationService = inject(TablePaginationService);
    private playerTypeLinkService = inject(PlayerTypeLinkService);
    private dialogService = inject(DialogService);
    private reportExecutionService = inject(ReportExecutionService);

    // Tournament ID from route
    tournamentId: string = '';

    // Loading states
    isImporting = false;
    importProgress = '';

    // Active tab for import
    activeImportTab: 'api' | 'xml' = 'api';

    // Field Import
    fullFieldApiUrl = 'http://api.lpga.com/';
    xmlFieldApiUrl = '';
    worldRankingsApiUrl = 'http://api.wwgr.net/v1.0';
    importFullField = true;
    importXmlField = false;
    importPointsRaceRankings = false;
    importWorldRankings = false;

    // Field Parameters
    maxFieldSize = signal<number>(120);
    aBracketSize = signal<number>(60);
    cBracketSize = signal<number>(0);

    // Computed B Bracket Size (read-only, calculated)
    bBracketSize = computed(() => {
        return this.maxFieldSize() - this.aBracketSize() - this.cBracketSize();
    });

    // Player Add/Edit
    playerForm: PlayerFormData = {
        tourId: '',
        shortName: '',
        firstName: '',
        middleName: '',
        lastName: '',
        countryId: 0,
        countryCode: '',
        stateId: 0,
        stateCode: '',
        hometown: '',
        lpgaId: '',
        lpgaAccountId: '',
        wwgrId: '',
        categoryId: 0,
        categoryName: '',
        fieldRank: '',
        aRank: '',
        abcType: '',
        r1Wave: '',
        isAmateur: false,
        isSponsorInvite: false,
        isQualifier: false,
        isTitleist: false,
        isCmeEligible: false
    };

    private readonly playerTypeFlagKeys: Array<keyof Pick<PlayerFormData, 'isAmateur' | 'isSponsorInvite' | 'isQualifier' | 'isTitleist' | 'isCmeEligible'>> = [
        'isAmateur',
        'isSponsorInvite',
        'isQualifier',
        'isTitleist',
        'isCmeEligible'
    ];

    private readonly playerTypeFlagDescriptions: Record<keyof Pick<PlayerFormData, 'isAmateur' | 'isSponsorInvite' | 'isQualifier' | 'isTitleist' | 'isCmeEligible'>, string> = {
        isAmateur: 'Amateur',
        isSponsorInvite: 'SponsorInvite',
        isQualifier: 'Qualifier',
        isTitleist: 'Titleist',
        isCmeEligible: 'CmePointsEligible'
    };

    private playerTypeDefinitionsCache: ModulesPlayersApplicationPlayerTypesPlayerTypeResponse[] | null = null;

    // Signals for reactive state management
    allPlayers = signal<TournamentPlayer[]>([]);
    lpgaTournamentId = signal<number | null>(null);
    tournamentName = signal<string | null>(null);
    hasLpgaTournamentId = computed(() => this.lpgaTournamentId() !== null);
    sortConfig = signal<SortConfig | null>(null);

    // Computed signal for player IDs already in this tournament
    existingPlayerIds = computed(() =>
        this.allPlayers()
            .map(p => p.playerId)
            .filter((id): id is string => !!id)
    );
    currentPage = signal<number>(1);
    pageSize = signal<number>(20);

    // Separate sort and pagination for alternates
    alternatesSortConfig = signal<SortConfig | null>(null);
    alternatesCurrentPage = signal<number>(1);
    alternatesPageSize = signal<number>(20);

    // Selection tracking
    selectedFieldPlayerId = signal<string | null>(null);
    selectedAlternatePlayerId = signal<string | null>(null);

    // Computed signals for button states
    canAddToField = computed(() => !!this.selectedAlternatePlayerId());
    canRemoveFromField = computed(() => !!this.selectedFieldPlayerId());
    canSwap = computed(() => !!this.selectedFieldPlayerId() && !!this.selectedAlternatePlayerId());
    canEditParameters = computed(() => this.allPlayers().length > 0);

    isLoading = signal<boolean>(false);
    isPrintingFieldList = signal<boolean>(false);
    isPrintingAlternates = signal<boolean>(false);

    canPrintFieldList = computed(() => !!this.tournamentId && !!this.tournamentName() && !this.isPrintingFieldList());
    canPrintAlternates = computed(() => !!this.tournamentId && !!this.tournamentName() && !this.isPrintingAlternates());

    // Computed values for sorting
    sortedPlayers = computed(() => {
        const players = this.allPlayers();
        const sort = this.sortConfig();

        if (sort) {
            return this.tableSortingService.sort(players, sort.key, sort.direction);
        }
        return players;
    });

    // Computed values for field players (non-alternates)
    fieldPlayers = computed(() => {
        return this.sortedPlayers().filter(p => p.type !== 'ALT');
    });

    // Pagination result for field players
    paginationResult = computed<PaginationResult<TournamentPlayer>>(() => {
        const players = this.fieldPlayers();
        const page = this.currentPage();
        const size = this.pageSize();
        return this.tablePaginationService.paginate(players, page, size);
    });

    // Computed alternates list (players with type=ALT)
    sortedAlternates = computed(() => {
        const alternates = this.sortedPlayers().filter(p => p.type === 'ALT');
        const sort = this.alternatesSortConfig();

        if (sort) {
            return this.tableSortingService.sort(alternates, sort.key, sort.direction);
        }
        return alternates;
    });

    // Pagination result for alternates
    alternatesPaginationResult = computed<PaginationResult<TournamentPlayer>>(() => {
        const alternates = this.sortedAlternates();
        const page = this.alternatesCurrentPage();
        const size = this.alternatesPageSize();
        return this.tablePaginationService.paginate(alternates, page, size);
    });

    // Display format for alternates table
    alternatePlayers = computed(() => {
        return this.alternatesPaginationResult().items.map((p, index) => ({
            number: ((this.alternatesCurrentPage() - 1) * this.alternatesPageSize()) + index + 1,
            rank: p.fieldRank ?? 0,
            category: p.categoryName ?? '',
            name: `${p.playerLastName}, ${p.playerFirstName}`,
            player: p  // Store original player data for editing
        }));
    });

    ngOnInit(): void {
        // Get tournament ID from route
        this.route.parent?.params.subscribe(params => {
            this.tournamentId = params['tournamentId'];
            if (this.tournamentId) {
                this.lpgaTournamentId.set(null);
                this.tournamentName.set(null);
                this.loadTournamentPlayers();
            }
        });
    }

    // Field Import Methods
    importField(): void {
        if (!this.tournamentId) {
            this.toastr.error('Please select a tournament before importing.', 'No Tournament Selected');
            return;
        }

        // Get tournament details and open the modal
        this.tournamentService.getTournamentByIdEndpoint(this.tournamentId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (tournament) => {
                    if (!tournament.importSourceId) {
                        this.toastr.error(
                            'This tournament does not have an LPGA Tournament ID. Please import the tournament first from the Tournaments page.',
                            'Missing LPGA ID'
                        );
                        return;
                    }

                    // Convert tournament to SourceTournament format for modal
                    const sourceTournament = {
                        tournamentId: tournament.importSourceId,
                        name: tournament.name || 'Tournament',
                        coursesCount: tournament.coursesCount || 0,
                        playersCount: tournament.playersCount || 0
                    };

                    this.importModal.openModal(sourceTournament);
                },
                error: (error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Error fetching tournament', error);
                    this.toastr.error('Failed to fetch tournament details. Please try again.', 'Error');
                }
            });
    }

    // Called when user confirms the import from modal
    onImportConfirmed(options: ImportOptions): void {
        // Get the tournament details to get LPGA ID
        this.tournamentService.getTournamentByIdEndpoint(this.tournamentId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (tournament) => {
                    if (!tournament.importSourceId) {
                        this.toastr.error(
                            'This tournament does not have an LPGA Tournament ID. Please import the tournament first from the Tournaments page.',
                            'Missing LPGA ID'
                        );
                        return;
                    }

                    // Update the component state with modal selections
                    this.importFullField = options.includePlayers;
                    this.importPointsRaceRankings = options.includePointsRaceRankings;

                    this.performImport(tournament.importSourceId, tournament.name || 'Tournament');
                },
                error: (error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Error fetching tournament', error);
                    this.toastr.error('Failed to fetch tournament details. Please try again.', 'Error');
                }
            });
    }

    private performImport(lpgaTournamentId: number, tournamentName: string): void {
        this.isImporting = true;
        const imports$ = [];

        // Get Points Race name from config
        const pointsRaceName = this.configService.config?.defaults?.import?.pointsRaceName || 'Race To CME Globe';

        // 1. Import Players (if checked)
        if (this.importFullField) {
            this.importProgress = 'Importing tournament field...';
            imports$.push(this.importService.importPlayers(lpgaTournamentId));
        }

        // 2. Import Points Race Rankings (if checked)
        if (this.importPointsRaceRankings) {
            this.importProgress = 'Importing points race rankings...';
            imports$.push(
                this.importService.importPointsRaceRankings(
                    lpgaTournamentId,
                    'LPGA',
                    pointsRaceName
                )
            );
        }

        if (imports$.length === 0) {
            this.toastr.warning(
                'Please select at least one import option.',
                'No Options Selected'
            );
            this.isImporting = false;
            return;
        }

        forkJoin(imports$)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => {
                    if (!this.importPointsRaceRankings) {
                        return of(null);
                    }

                    this.importProgress = 'Importing missing points race players...';

                    return this.importService.importMissingPointsRacePlayers(lpgaTournamentId, 'LPGA')
                        .pipe(
                            catchError((error) => {
                                this.loggingService.warn('TournamentPlayersComponent', 'Error importing missing points race players', error);
                                return of(null); // continue even if this fails
                            })
                        );
                })
            )
            .subscribe({
                next: () => {
                    this.completeImport(tournamentName);
                },
                error: (error) => {
                    this.isImporting = false;
                    this.importProgress = '';
                    this.loggingService.error('TournamentPlayersComponent', 'Import failed', error);
                    this.toastr.error(
                        'Failed to import tournament field. Please check the logs and try again.',
                        'Import Failed'
                    );
                }
            });
    }

    private completeImport(tournamentName: string): void {
        this.isImporting = false;
        this.importProgress = '';

        const successMessage = this.importPointsRaceRankings
            ? `Field and Points Race Rankings imported successfully for ${tournamentName}!`
            : `Field imported successfully for ${tournamentName}!`;

        this.toastr.success(successMessage, 'Import Complete', {
            timeOut: 5000
        });

        // Reload tournament players
        this.loadTournamentPlayers();
    }

    private loadTournamentPlayers(): void {
        if (!this.tournamentId) {
            return;
        }

        this.isLoading.set(true);

        // Load both tournament players and tournament settings
        forkJoin({
            players: this.tournamentService.getTournamentPlayers(this.tournamentId),
            tournament: this.tournamentService.getTournamentByIdEndpoint(this.tournamentId)
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ players, tournament }) => {
                    this.allPlayers.set(players);

                    const importSourceIdRaw = (tournament as { importSourceId?: number | string | null | undefined }).importSourceId;
                    const parsedImportSourceId = typeof importSourceIdRaw === 'number'
                        ? importSourceIdRaw
                        : typeof importSourceIdRaw === 'string'
                            ? Number(importSourceIdRaw)
                            : null;
                    const normalizedImportSourceId = typeof parsedImportSourceId === 'number' && !Number.isNaN(parsedImportSourceId) && parsedImportSourceId > 0
                        ? parsedImportSourceId
                        : null;
                    this.tournamentName.set((tournament.name ?? '').trim() || null);
                    this.lpgaTournamentId.set(normalizedImportSourceId);

                    // Load field parameters from tournament settings
                    if (tournament.settings) {
                        const maxField = tournament.settings['Max_Field_Size']
                            || tournament.settings['MaxFieldSize']
                            || tournament.settings['Number_Of_Players_Max'];
                        const aBracket = tournament.settings['Number_Of_A_Players'];
                        const cBracket = tournament.settings['Number_Of_C_Players'];

                        if (maxField) this.maxFieldSize.set(parseInt(maxField, 10));
                        if (aBracket) this.aBracketSize.set(parseInt(aBracket, 10));
                        if (cBracket) this.cBracketSize.set(parseInt(cBracket, 10));
                    }

                    this.isLoading.set(false);
                    this.loggingService.info('TournamentPlayersComponent', `Loaded ${players.length} tournament players`);
                },
                error: (error) => {
                    this.isLoading.set(false);
                    this.loggingService.error('TournamentPlayersComponent', 'Error loading tournament players', error);
                    this.toastr.error('Failed to load tournament players. Please try again.', 'Load Failed');
                }
            });
    }

    private reloadPlayersOnly(): void {
        if (!this.tournamentId) {
            return;
        }

        this.isLoading.set(true);

        // Only reload players list, keep existing field parameters in signals
        this.tournamentService.getTournamentPlayers(this.tournamentId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (players) => {
                    this.allPlayers.set(players);
                    this.isLoading.set(false);
                    this.loggingService.info('TournamentPlayersComponent', `Reloaded ${players.length} tournament players`);
                },
                error: (error) => {
                    this.isLoading.set(false);
                    this.loggingService.error('TournamentPlayersComponent', 'Error reloading tournament players', error);
                    this.toastr.error('Failed to reload tournament players. Please try again.', 'Load Failed');
                }
            });
    }

    // Sorting handler
    onSort(config: SortConfig): void {
        this.sortConfig.set(config);
        this.currentPage.set(1); // Reset to first page when sorting
    }

    // Pagination handler
    onPageChange(event: PageChangeEvent): void {
        this.currentPage.set(event.page);
        this.pageSize.set(event.pageSize);
    }

    // Field Parameters Methods
    openFieldParametersModal(): void {
        this.fieldParametersModal.openModal({
            maxFieldSize: this.maxFieldSize(),
            aBracketSize: this.aBracketSize(),
            cBracketSize: this.cBracketSize()
        });
    }

    onFieldParametersSaved(values: FieldParameterValues): void {
        this.maxFieldSize.set(Number(values.maxFieldSize) || 0);
        this.aBracketSize.set(Number(values.aBracketSize) || 0);
        this.cBracketSize.set(Number(values.cBracketSize) || 0);

        // Persist immediately without validation
        this.saveFieldParameters();
    }

    private saveFieldParameters(): void {
        if (!this.tournamentId) {
            this.toastr.error('No tournament selected', 'Error');
            return;
        }

        const maxField = this.maxFieldSize();
        const aBracket = this.aBracketSize();
        const cBracket = this.cBracketSize();
        const calculatedBBracket = this.bBracketSize();

        // Get current tournament
        this.tournamentService.getTournamentByIdEndpoint(this.tournamentId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((tournament) => {
                    // Update settings with field parameters
                    const updatedTournament: Tournament = {
                        ...tournament,
                        settings: {
                            ...tournament.settings,
                            'Number_Of_Players_Max': maxField.toString(),
                            'Number_Of_A_Players': aBracket.toString(),
                            'Number_Of_B_Players': calculatedBBracket.toString(),
                            'Number_Of_C_Players': cBracket.toString()
                        }
                    };

                    // Save tournament with updated settings
                    return this.tournamentService.updateTournament(this.tournamentId, updatedTournament);
                }),
                // After saving settings, assign brackets to update player types
                switchMap(() => {
                    return this.tournamentService.tournamentsTournamentIdPlayersAssignBracketsPost(
                        this.tournamentId,
                        {
                            maxFieldSize: maxField,
                            aBracketSize: aBracket,
                            bBracketSize: calculatedBBracket,
                            cBracketSize: cBracket
                        }
                    );
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success(
                        `Field parameters saved and brackets assigned: Max=${maxField}, A=${aBracket}, B=${calculatedBBracket}, C=${cBracket}`,
                        'Parameters Saved'
                    );
                    // Reload players list to show updated bracket types
                    this.reloadPlayersOnly();
                },
                error: (error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Error saving field parameters', error);
                    this.toastr.error(
                        error.error?.error || error.message || 'Failed to save field parameters. Please try again.',
                        'Save Failed'
                    );
                }
            });
    }

    assignBrackets(): void {
        console.log('Assign brackets clicked');

        if (!this.tournamentId) {
            this.toastr.error('No tournament selected', 'Error');
            return;
        }

        const calculatedBBracket = this.bBracketSize();
        const maxField = this.maxFieldSize();
        const aBracket = this.aBracketSize();
        const cBracket = this.cBracketSize();

        // Validate bracket sizes
        if (aBracket + calculatedBBracket + cBracket !== maxField) {
            this.toastr.error(
                `Bracket sizes (A: ${aBracket} + B: ${calculatedBBracket} + C: ${cBracket}) must equal Max Field Size (${maxField})`,
                'Invalid Bracket Configuration'
            );
            return;
        }

        // Save parameters first, then reload to assign player types
        this.tournamentService.getTournamentByIdEndpoint(this.tournamentId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((tournament) => {
                    // Update settings with field parameters
                    const updatedTournament: Tournament = {
                        ...tournament,
                        settings: {
                            ...tournament.settings,
                            'Number_Of_Players_Max': maxField.toString(),
                            'Number_Of_A_Players': aBracket.toString(),
                            'Number_Of_B_Players': calculatedBBracket.toString(),
                            'Number_Of_C_Players': cBracket.toString()
                        }
                    };

                    // Save tournament with updated settings, then assign brackets
                    return this.tournamentService.updateTournament(this.tournamentId, updatedTournament).pipe(
                        switchMap(() => this.tournamentService.assignPlayerBrackets(
                            this.tournamentId,
                            maxField,
                            aBracket,
                            calculatedBBracket,
                            cBracket
                        ))
                    );
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success(
                        `Brackets assigned successfully! Players have been assigned types (A, B, C, ALT) based on their field rankings (using A/B rank as a fallback). Max Field=${maxField}, A=${aBracket}, B=${calculatedBBracket}, C=${cBracket}.`,
                        'Brackets Assigned'
                    );
                    // Reload to get updated settings and player types
                    this.loadTournamentPlayers();
                },
                error: (error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Error assigning brackets', error);
                    this.toastr.error(
                        error.error?.title || error.message || 'Failed to assign brackets. Please try again.',
                        'Assignment Failed'
                    );
                }
            });
    }

    // Player Add/Edit Methods
    openPlayerModal(player?: TournamentPlayer): void {
        if (player && player.playerId) {
            this.tournamentService.getTournamentPlayer(this.tournamentId, player.playerId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (tournamentPlayer) => {
                        const playerFormData = this.convertTournamentPlayerToFormData(tournamentPlayer);
                        this.playerModal.openModal(playerFormData);
                    },
                    error: (error) => {
                        this.loggingService.error('TournamentPlayersComponent', 'Error fetching tournament player', error);
                        const playerFormData = this.convertTournamentPlayerToFormData(player);
                        this.playerModal.openModal(playerFormData);
                    }
                });
        } else {
            this.playerModal.openModal();
        }
    }

    editPlayer(player: TournamentPlayer): void {
        this.openPlayerModal(player);
    }

    editAlternate(alternate: { number: number; rank: number; category: string; name: string; player: TournamentPlayer }): void {
        this.openPlayerModal(alternate.player);
    }

    deletePlayerFromField(player: TournamentPlayer): void {
        this.confirmationDialog.open({
            title: 'Remove Player',
            message: `Are you sure you want to remove <strong>${player.playerFirstName} ${player.playerLastName}</strong> from the tournament field?<br><br><strong>Warning:</strong> This will permanently delete all associated scorecards, scores, shots, and group assignments for this player.`,
            confirmButtonText: 'Remove Player',
            cancelButtonText: 'Cancel',
            dangerMode: true,
            data: player
        });
    }

    deleteAlternateFromTournament(player: TournamentPlayer): void {
        this.confirmationDialog.open({
            title: 'Remove Alternate',
            message: `Are you sure you want to remove <strong>${player.playerFirstName} ${player.playerLastName}</strong> from the alternates list?<br><br><strong>Warning:</strong> This will permanently delete all associated scorecards, scores, shots, and group assignments for this player.`,
            confirmButtonText: 'Remove Alternate',
            cancelButtonText: 'Cancel',
            dangerMode: true,
            data: player
        });
    }

    onDeletePlayerConfirmed(player: TournamentPlayer): void {
        if (!player.playerId || !this.tournamentId) {
            this.toastr.error('Missing required player or tournament information', 'Error');
            return;
        }

        const isAlternate = player.type === 'ALT';
        const playerType = isAlternate ? 'alternate' : 'tournament field';
        const successTitle = isAlternate ? 'Alternate Removed' : 'Player Removed';

        this.tournamentService.deleteTournamentPlayer(this.tournamentId, player.playerId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.toastr.success(
                        `${player.playerFirstName} ${player.playerLastName} has been removed from the ${playerType}.`,
                        successTitle
                    );
                    // Reload players
                    this.loadTournamentPlayers();
                },
                error: (error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Error removing player', error);
                    this.toastr.error(
                        error.error?.error || error.message || `Failed to remove player from ${playerType}. Please try again.`,
                        'Remove Failed'
                    );
                }
            });
    }

    private convertTournamentPlayerToFormData(player: TournamentPlayer): PlayerFormData {
        return {
            playerId: player.playerId,
            tournamentId: player.tournamentId,
            tourId: player.tourId || '',
            shortName: player.playerShortName || '',
            firstName: player.playerFirstName || '',
            middleName: player.playerMiddleName || '',
            lastName: player.playerLastName || '',
            countryId: player.playerCountryId || 0,
            countryCode: player.playerCountryCode || '',
            stateId: player.playerStateId || 0,
            stateCode: player.playerStateCode || '',
            hometown: player.hometown || '',
            lpgaId: player.lpgaId !== undefined && player.lpgaId !== null ? player.lpgaId.toString() : '',
            lpgaAccountId: (player as any).lpgaAccountId !== undefined && (player as any).lpgaAccountId !== null
                ? (player as any).lpgaAccountId.toString()
                : '',
            wwgrId: player.wwgrId !== undefined && player.wwgrId !== null ? player.wwgrId.toString() : '',
            categoryId: player.categoryId || 0,
            categoryName: player.categoryName || '',
            fieldRank: player.fieldRank?.toString() || '',
            aRank: player.abrank?.toString() || '',
            abcType: player.type || '',
            r1Wave: player.r1Wave || '',
            isAmateur: player.isAmateur ?? false,
            isSponsorInvite: player.isSponsorInvite ?? false,
            isQualifier: player.isQualifier ?? false,
            isTitleist: player.isTitleist ?? false,
            isCmeEligible: player.isCmePointsEligible ?? false
        };
    }

    onPlayerSaved(playerData: PlayerFormData): void {
        if (!this.tournamentId) {
            this.toastr.error('No tournament selected', 'Error');
            return;
        }

        // Check if this is editing an existing tournament player or adding a new one
        if (playerData.playerId && playerData.tournamentId) {
            // Edit mode - update both Player and TournamentPlayer
            this.updatePlayerAndTournamentPlayer(playerData);
        } else if (playerData.playerId) {
            // Existing player - just associate with tournament
            this.associateExistingPlayerWithTournament(playerData);
        } else {
            // New player - create player first, then associate
            this.createNewPlayerAndAssociate(playerData);
        }
    }

    private updatePlayerAndTournamentPlayer(playerData: PlayerFormData): void {
        if (!playerData.playerId || !playerData.tournamentId) {
            this.toastr.error('Player ID and Tournament ID are required', 'Error');
            return;
        }

        this.loggingService.info('TournamentPlayersComponent', 'Updating player and tournament player', {
            playerId: playerData.playerId,
            tournamentId: playerData.tournamentId
        });

        // First, fetch the existing player to get required fields like tourId
        this.playersService.getPlayerByIdEndpoint(playerData.playerId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((existingPlayer) => {
                    // Step 1: Update the Player entity
                    const updatePlayerRequest: ModulesPlayersEndpointsContractsPlayersUpdatePlayerRequestModel = {
                        tourId: playerData.tourId || existingPlayer.tourId || '00000000-0000-0000-0000-000000000000',
                        wwgrId: parseInt(playerData.wwgrId) || existingPlayer.wwgrId || 0,
                        firstName: playerData.firstName || existingPlayer.firstName || '',
                        middleName: playerData.middleName || existingPlayer.middleName || '',
                        lastName: playerData.lastName || existingPlayer.lastName || '',
                        playFirstName: playerData.firstName || existingPlayer.playFirstName || existingPlayer.firstName || '',
                        playMiddleName: playerData.middleName || existingPlayer.playMiddleName || existingPlayer.middleName || '',
                        playLastName: playerData.lastName || existingPlayer.playLastName || existingPlayer.lastName || '',
                        shortName: playerData.shortName || existingPlayer.shortName || '',
                        gender: existingPlayer.gender || 'F',
                        city: existingPlayer.city || '',
                        stateId: playerData.stateId || existingPlayer.stateId || 0,
                        countryId: playerData.countryId || existingPlayer.countryId || 0,
                        hometown: playerData.hometown || existingPlayer.hometown || '',
                        isActive: existingPlayer.isActive ?? true,
                        image: existingPlayer.image || '',
                        lpgaId: parseInt(playerData.lpgaId, 10) || existingPlayer.lpgaId || 0,
                        lpgaAccountId: parseInt(playerData.lpgaAccountId, 10) || existingPlayer.lpgaAccountId || 0
                    };

                    return this.playersService.playersPlayerIdPut(playerData.playerId!, updatePlayerRequest);
                }),
                switchMap(() => {
                    // Step 2: Update the TournamentPlayer entity
                    const tournamentPlayerUpdate: TournamentPlayer = {
                        tournamentId: playerData.tournamentId,
                        playerId: playerData.playerId,
                        categoryId: playerData.categoryId || 0,
                        categoryRank: parseInt(playerData.fieldRank) || 0,
                        fieldRank: parseInt(playerData.fieldRank) || 0,
                        abrank: parseInt(playerData.aRank) || 0,
                        money: 0,
                        r1Wave: playerData.r1Wave || undefined
                    };

                    return this.tournamentService.updateTournamentPlayer(
                        playerData.tournamentId!,
                        playerData.playerId!,
                        tournamentPlayerUpdate
                    );
                }),
                switchMap(() => {
                    return this.syncPlayerTypeFlags(playerData.playerId!, playerData);
                }),
                catchError((error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Failed to update player', error);
                    this.toastr.error(
                        error.error?.title || error.message || 'Failed to update player',
                        'Update Failed'
                    );
                    return of(null);
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Player updated successfully', 'Success');
                    this.loadTournamentPlayers();
                }
            });
    }

    private createNewPlayerAndAssociate(playerData: PlayerFormData): void {
        // Step 1: Create the Player
        const createPlayerRequest: ModulesPlayersEndpointsContractsPlayersCreatePlayerRequest = {
            tourId: playerData.tourId,
            wwgrId: parseInt(playerData.wwgrId) || 0,
            firstName: playerData.firstName,
            middleName: playerData.middleName || '',
            lastName: playerData.lastName,
            playFirstName: playerData.firstName, // Using same as firstName for now
            playMiddleName: playerData.middleName || '',
            playLastName: playerData.lastName, // Using same as lastName for now
            shortName: playerData.shortName,
            gender: 'F', // TODO: Get from form or default
            city: '', // TODO: Add to form if needed
            stateId: playerData.stateId || 0,
            countryId: playerData.countryId || 0,
            hometown: playerData.hometown || '',
            isActive: true,
            image: '',
            lpgaId: parseInt(playerData.lpgaId, 10) || 0,
            lpgaAccountId: parseInt(playerData.lpgaAccountId, 10) || 0
        };

        this.loggingService.info('TournamentPlayersComponent', 'Creating new player', createPlayerRequest);

        this.playersService.playersPost(createPlayerRequest)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((playerId: string) => {
                    this.loggingService.info('TournamentPlayersComponent', 'Player created successfully', { playerId });
                    playerData.playerId = playerId;
                    playerData.tournamentId = this.tournamentId;
                    // Step 2: Associate the player with the tournament
                    return this.createTournamentPlayer(playerId, playerData).pipe(
                        switchMap(() => this.syncPlayerTypeFlags(playerId, playerData))
                    );
                }),
                catchError((error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Failed to create player', error);
                    this.toastr.error(
                        error.error?.title || error.message || 'Failed to create player',
                        'Player Creation Failed'
                    );
                    return of(null);
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Player created and added to tournament', 'Success');
                    this.loadTournamentPlayers();
                }
            });
    }

    private associateExistingPlayerWithTournament(playerData: PlayerFormData): void {
        if (!playerData.playerId) {
            this.toastr.error('Player ID is required', 'Error');
            return;
        }

        playerData.tournamentId = this.tournamentId;

        this.loggingService.info('TournamentPlayersComponent', 'Associating existing player with tournament', {
            playerId: playerData.playerId,
            tournamentId: this.tournamentId
        });

        // First, fetch the existing player to get required fields
        this.playersService.getPlayerByIdEndpoint(playerData.playerId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((existingPlayer) => {
                    // Update the Player entity with any changes made in the form
                    const updatePlayerRequest: ModulesPlayersEndpointsContractsPlayersUpdatePlayerRequestModel = {
                        tourId: playerData.tourId || existingPlayer.tourId || '00000000-0000-0000-0000-000000000000',
                        wwgrId: parseInt(playerData.wwgrId) || existingPlayer.wwgrId || 0,
                        firstName: playerData.firstName || existingPlayer.firstName || '',
                        middleName: playerData.middleName || existingPlayer.middleName || '',
                        lastName: playerData.lastName || existingPlayer.lastName || '',
                        playFirstName: playerData.firstName || existingPlayer.playFirstName || existingPlayer.firstName || '',
                        playMiddleName: playerData.middleName || existingPlayer.playMiddleName || existingPlayer.middleName || '',
                        playLastName: playerData.lastName || existingPlayer.playLastName || existingPlayer.lastName || '',
                        shortName: playerData.shortName || existingPlayer.shortName || '',
                        gender: existingPlayer.gender || 'F',
                        city: existingPlayer.city || '',
                        stateId: playerData.stateId || existingPlayer.stateId || 0,
                        countryId: playerData.countryId || existingPlayer.countryId || 0,
                        hometown: playerData.hometown || existingPlayer.hometown || '',
                        isActive: existingPlayer.isActive ?? true,
                        image: existingPlayer.image || '',
                        lpgaId: parseInt(playerData.lpgaId, 10) || existingPlayer.lpgaId || 0,
                        lpgaAccountId: parseInt(playerData.lpgaAccountId, 10) || existingPlayer.lpgaAccountId || 0
                    };

                    return this.playersService.playersPlayerIdPut(playerData.playerId!, updatePlayerRequest);
                }),
                switchMap(() => {
                    // Then create the TournamentPlayer association
                    return this.createTournamentPlayer(playerData.playerId!, playerData).pipe(
                        switchMap(() => this.syncPlayerTypeFlags(playerData.playerId!, playerData))
                    );
                }),
                catchError((error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Failed to associate player', error);
                    this.toastr.error(
                        error.error?.title || error.message || 'Failed to add player to tournament',
                        'Association Failed'
                    );
                    return of(null);
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success('Player added to tournament', 'Success');
                    this.loadTournamentPlayers();
                }
            });
    }

    private createTournamentPlayer(playerId: string, playerData: PlayerFormData): Observable<number> {
        const request = {
            categoryId: playerData.categoryId || 0,
            categoryRank: parseInt(playerData.fieldRank) || 0,
            fieldRank: parseInt(playerData.fieldRank) || 0,
            abrank: parseInt(playerData.aRank) || 0,
            money: 0,
            r1Wave: playerData.r1Wave || null
        };

        this.loggingService.info('TournamentPlayersComponent', 'Creating tournament player', {
            tournamentId: this.tournamentId,
            playerId,
            request
        });

        return this.tournamentService.createTournamentPlayer(
            this.tournamentId,
            playerId,
            request
        );
    }

    private getPlayerTypeDefinitions(): Observable<ModulesPlayersApplicationPlayerTypesPlayerTypeResponse[]> {
        if (this.playerTypeDefinitionsCache) {
            return of(this.playerTypeDefinitionsCache);
        }

        return this.playersService.getPlayerTypesEndpoint().pipe(
            tap((types) => {
                this.playerTypeDefinitionsCache = types;
            })
        );
    }

    private syncPlayerTypeFlags(playerId: string, playerData: PlayerFormData): Observable<void> {
        if (!playerId) {
            return of(undefined as void);
        }

        return this.getPlayerTypeDefinitions().pipe(
            switchMap((definitions) => {
                const relevantDefinitions = this.playerTypeFlagKeys.reduce((acc, key) => {
                    const description = this.playerTypeFlagDescriptions[key];
                    const match = definitions.find((type) => this.normalizeTypeKey(type.description, type.name) === description.toLowerCase());

                    if (!match || typeof match.playerTypeId !== 'number') {
                        this.loggingService.warn('TournamentPlayersComponent', `Player type definition not found for flag ${key} (${description}).`);
                        return acc;
                    }

                    acc.set(description.toLowerCase(), match);
                    return acc;
                }, new Map<string, ModulesPlayersApplicationPlayerTypesPlayerTypeResponse>());

                return this.playersService.getPlayerTypesByIdEndpoint(playerId).pipe(
                    switchMap((existingTypes) => {
                        const existingMap = new Map<string, ModulesPlayersApplicationPlayerTypesPlayerTypeResponse>(
                            existingTypes
                                .filter((type) => !!type.description || !!type.name)
                                .map((type) => [this.normalizeTypeKey(type.description, type.name), type] as const)
                        );

                        const operations: Observable<unknown>[] = [];

                        this.playerTypeFlagKeys.forEach((flagKey) => {
                            const description = this.playerTypeFlagDescriptions[flagKey];
                            const normalizedKey = description.toLowerCase();
                            const desired = !!playerData[flagKey];
                            const definition = relevantDefinitions.get(normalizedKey);
                            const existing = existingMap.get(normalizedKey);

                            if (desired) {
                                if (!definition) {
                                    return;
                                }

                                if (!existing) {
                                    operations.push(
                                        this.playersService.playersPlayerIdTypesPost(playerId, {
                                            playerTypeId: definition.playerTypeId
                                        })
                                    );
                                }
                            } else if (existing?.linkId !== undefined && existing.linkId !== null) {
                                operations.push(this.playerTypeLinkService.remove(existing.linkId));
                            }
                        });

                        if (!operations.length) {
                            return of(undefined as void);
                        }

                        return forkJoin(operations).pipe(map(() => undefined as void));
                    })
                );
            }),
            catchError((error) => {
                this.loggingService.error('TournamentPlayersComponent', 'Failed to sync player type flags', error);
                return throwError(() => error);
            })
        );
    }

    private normalizeTypeKey(description?: string | null, name?: string | null): string {
        return (description || name || '').toLowerCase();
    }

    newPlayer(): void {
        this.playerForm = {
            tourId: '',
            shortName: '',
            firstName: '',
            middleName: '',
            lastName: '',
            countryId: 0,
            countryCode: '',
            stateId: 0,
            stateCode: '',
            hometown: '',
            lpgaId: '',
            lpgaAccountId: '',
            wwgrId: '',
            categoryId: 0,
            categoryName: '',
            fieldRank: '',
            aRank: '',
            abcType: '',
            r1Wave: '',
            isAmateur: false,
            isSponsorInvite: false,
            isQualifier: false,
            isTitleist: false,
            isCmeEligible: false
        };
    }

    savePlayer(): void {
        console.log('Save player clicked', this.playerForm);
        // TODO: Implement save functionality
    }

    deletePlayer(): void {
        console.log('Delete player clicked');
        // TODO: Implement delete functionality
    }

    // Field List Methods
    addPlayerToField(playerId?: string): void {
        const playerIdToAdd = playerId || this.selectedAlternatePlayerId();
        if (!playerIdToAdd) {
            this.toastr.error('Please select an alternate player to add to the field', 'No Player Selected');
            return;
        }

        if (!this.tournamentId) {
            this.toastr.error('No tournament selected', 'Error');
            return;
        }

        // Find the player to get their name for the success message
        const player = this.allPlayers().find(p => p.playerId === playerIdToAdd);
        const playerName = player ? `${player.playerFirstName} ${player.playerLastName}` : 'Player';

        this.tournamentService.moveAlternateToField(this.tournamentId, playerIdToAdd)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.toastr.success(
                        `${playerName} has been moved from alternates to the main field.`,
                        'Player Added to Field'
                    );
                    this.selectedAlternatePlayerId.set(null); // Clear selection
                    this.reloadPlayersOnly();
                },
                error: (error) => {
                    this.loggingService.error('TournamentPlayersComponent', 'Error adding player to field', error);
                    this.toastr.error(
                        error.error?.error || error.message || 'Failed to add player to field. Please try again.',
                        'Operation Failed'
                    );
                }
            });
    }

    swapPlayers(fieldPlayerId?: string, alternatePlayerId?: string): void {
        const fieldPlayerIdToSwap = fieldPlayerId || this.selectedFieldPlayerId();
        const alternatePlayerIdToSwap = alternatePlayerId || this.selectedAlternatePlayerId();

        if (!fieldPlayerIdToSwap || !alternatePlayerIdToSwap) {
            this.toastr.error('Please select both a field player and an alternate to swap', 'Invalid Selection');
            return;
        }

        if (!this.tournamentId) {
            this.toastr.error('No tournament selected', 'Error');
            return;
        }

        // Get player names for messages
        const fieldPlayer = this.allPlayers().find(p => p.playerId === fieldPlayerIdToSwap);
        const alternatePlayer = this.allPlayers().find(p => p.playerId === alternatePlayerIdToSwap);
        const fieldPlayerName = fieldPlayer ? `${fieldPlayer.playerFirstName} ${fieldPlayer.playerLastName}` : 'Field Player';
        const alternatePlayerName = alternatePlayer ? `${alternatePlayer.playerFirstName} ${alternatePlayer.playerLastName}` : 'Alternate';

        // First attempt without confirmation
        this.performSwap(fieldPlayerIdToSwap, alternatePlayerIdToSwap, false, fieldPlayerName, alternatePlayerName);
    }

    private performSwap(
        fieldPlayerId: string,
        alternatePlayerId: string,
        confirmed: boolean,
        fieldPlayerName: string,
        alternatePlayerName: string
    ): void {
        this.tournamentService.swapPlayerWithAlternate(
            this.tournamentId,
            fieldPlayerId,
            alternatePlayerId,
            confirmed
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.toastr.success(
                        `${fieldPlayerName} and ${alternatePlayerName} have been swapped successfully.`,
                        'Players Swapped'
                    );
                    this.selectedFieldPlayerId.set(null); // Clear selections
                    this.selectedAlternatePlayerId.set(null);
                    this.reloadPlayersOnly();
                },
                error: async (error) => {
                    // Log full error details for debugging
                    console.log('Swap error details:', {
                        status: error.status,
                        statusText: error.statusText,
                        error: error.error,
                        message: error.message,
                        url: error.url
                    });
                    console.log('Error object:', error.error);
                    console.log('Error errors:', error.error?.errors);

                    // Check if this is a scorecard transfer validation error
                    if (!confirmed && error.error?.error?.includes('scorecard')) {
                        // Show confirmation dialog
                        const message = `${fieldPlayerName} has existing scorecards that will be transferred to ${alternatePlayerName}. Do you want to proceed?`;
                        const confirmedTransfer = await this.dialogService.confirm(message, {
                            title: 'Transfer Scorecards?',
                            confirmLabel: 'Transfer & Swap',
                            cancelLabel: 'Cancel'
                        });

                        if (confirmedTransfer) {
                            // Retry with confirmation
                            this.performSwap(fieldPlayerId, alternatePlayerId, true, fieldPlayerName, alternatePlayerName);
                        } else {
                            this.toastr.info('Swap cancelled', 'Cancelled');
                        }
                    } else {
                        this.loggingService.error('TournamentPlayersComponent', 'Error swapping players', error);
                        const errorDetail = error.error?.detail || error.error?.title || error.error?.error || error.message || 'Failed to swap players. Please try again.';
                        this.toastr.error(
                            typeof errorDetail === 'object' ? JSON.stringify(errorDetail) : errorDetail,
                            'Swap Failed'
                        );
                    }
                }
            });
    }

    removePlayerFromField(playerId?: string): void {
        const playerIdToRemove = playerId || this.selectedFieldPlayerId();
        if (!playerIdToRemove) {
            this.toastr.error('Please select a field player to move to alternates', 'No Player Selected');
            return;
        }

        if (!this.tournamentId) {
            this.toastr.error('No tournament selected', 'Error');
            return;
        }

        // Get player name for messages
        const player = this.allPlayers().find(p => p.playerId === playerIdToRemove);
        const playerName = player ? `${player.playerFirstName} ${player.playerLastName}` : 'Player';

        // First attempt without confirmation
        this.performMoveToAlternates(playerIdToRemove, false, playerName);
    }

    private performMoveToAlternates(playerId: string, confirmed: boolean, playerName: string): void {
        this.tournamentService.movePlayerToAlternates(this.tournamentId, playerId, confirmed)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    const message = confirmed
                        ? `${playerName} has been moved to alternates and all scorecards have been deleted.`
                        : `${playerName} has been moved to alternates.`;
                    this.toastr.success(message, 'Player Moved to Alternates');
                    this.selectedFieldPlayerId.set(null); // Clear selection
                    this.reloadPlayersOnly();
                },
                error: async (error) => {
                    // Check if this is a scorecard deletion validation error
                    if (!confirmed && error.error?.error?.includes('scorecard')) {
                        // Show confirmation dialog
                        const message = `${playerName} has existing scorecards. Moving to alternates will permanently delete all associated scorecards, scores, shots, and group assignments. Do you want to proceed?`;
                        const confirmedDelete = await this.dialogService.confirm(message, {
                            title: 'Delete Scorecards?',
                            confirmLabel: 'Delete & Move',
                            cancelLabel: 'Cancel'
                        });

                        if (confirmedDelete) {
                            // Retry with confirmation
                            this.performMoveToAlternates(playerId, true, playerName);
                        } else {
                            this.toastr.info('Operation cancelled', 'Cancelled');
                        }
                    } else {
                        this.loggingService.error('TournamentPlayersComponent', 'Error moving player to alternates', error);
                        this.toastr.error(
                            error.error?.error || error.message || 'Failed to move player to alternates. Please try again.',
                            'Operation Failed'
                        );
                    }
                }
            });
    }

    // XML Import Methods
    importFromXml(): void {
        this.xmlImportModal.openModal(this.tournamentId);
    }

    onXmlImportConfirmed(options: XmlImportOptions): void {
        this.isImporting = true;
        this.importProgress = 'Uploading XML file...';

        console.log('XML Import confirmed:', {
            fileName: options.file.name,
            fileSize: options.file.size,
            tournamentId: options.tournamentId
        });

        this.importService.importPlayersFromXml(this.tournamentId, options.file, false)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response: ModulesImportsEndpointsPlayersImportPlayersFromXmlResponse) => {
                    console.log('XML Import response:', response);
                    this.isImporting = false;
                    this.importProgress = '';

                    const typedResponse = (response ?? {}) as XmlImportResponse;
                    const importedCount = typedResponse.playerCount ?? 0;
                    const failures = typedResponse.failures ?? [];
                    const failedCount = typedResponse.failedCount ?? failures.length;
                    const totalCount = typedResponse.totalPlayerCount ?? importedCount + failedCount;
                    const summaryMessage = typedResponse.message
                        || (failedCount > 0
                            ? `Imported ${importedCount} of ${totalCount} player(s). ${failedCount} failed.`
                            : `Successfully imported ${importedCount} player(s) from XML file.`);
                    const hasFailures = failedCount > 0;

                    if (hasFailures) {
                        const { html: previewHtml, truncated } = this.buildFailurePreview(failures);
                        const warningHtml = `${this.escapeHtml(summaryMessage)}${previewHtml}`;

                        this.toastr.warning(warningHtml, 'Import Completed with Issues', {
                            enableHtml: true,
                            closeButton: true,
                            tapToDismiss: false,
                            timeOut: 15000
                        });

                        if (truncated) {
                            this.toastr.info('See the browser console for the full list of rejected players.', 'Additional Details', {
                                timeOut: 8000
                            });
                        }

                        if (failures.length > 0) {
                            this.loggingService.warn('TournamentPlayersComponent', 'XML import completed with failures', {
                                summaryMessage,
                                importedCount,
                                failedCount,
                                totalCount,
                                failures
                            });
                            if (console && typeof console.table === 'function') {
                                console.table(failures);
                            }
                        }
                    } else {
                        this.toastr.success(summaryMessage, 'Import Successful', {
                            timeOut: 5000
                        });
                    }

                    // Clear cache and reload players
                    console.log('Clearing cache and reloading players...');
                    this.loadTournamentPlayers();
                },
                error: (error) => {
                    console.error('XML Import error:', error);
                    this.isImporting = false;
                    this.importProgress = '';
                    this.loggingService.error('TournamentPlayersComponent', 'XML import failed', error);

                    const problemDetails = error?.error ?? {};
                    const problemErrors = problemDetails?.errors;
                    let detail: string = problemDetails?.detail
                        || problemDetails?.title
                        || problemDetails?.error
                        || error.message
                        || 'Failed to import players from XML file.';

                    if (problemErrors && typeof problemErrors === 'object') {
                        const aggregated = Object.entries(problemErrors)
                            .flatMap(([, value]) => Array.isArray(value) ? value : [value])
                            .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
                            .join(' ');

                        if (aggregated) {
                            detail = `${detail} ${aggregated}`.trim();
                        }
                    }

                    this.toastr.error(detail, 'Import Failed', {
                        timeOut: 8000,
                        closeButton: true
                    });
                }
            });
    }

    private buildFailurePreview(failures: XmlPlayerImportFailure[]): { html: string; truncated: boolean } {
        if (!failures.length) {
            return { html: '', truncated: false };
        }

        const previewLimit = 5;
        const previewItems = failures.slice(0, previewLimit).map((failure) => {
            const trimmedName = failure.playerName?.trim();
            const hasName = !!trimmedName && trimmedName.length > 0;
            const baseLabel = hasName
                ? trimmedName!
                : (typeof failure.wwgrId === 'number' && failure.wwgrId > 0 ? `WWGR ${failure.wwgrId}` : 'Unknown player');
            const displayLabel = hasName && typeof failure.wwgrId === 'number' && failure.wwgrId > 0
                ? `${baseLabel} (WWGR ${failure.wwgrId})`
                : baseLabel;

            const trimmedMessage = failure.errorMessage?.trim();
            const trimmedCode = failure.errorCode?.trim();
            const reasonSource = trimmedMessage && trimmedMessage.length > 0
                ? trimmedMessage
                : trimmedCode && trimmedCode.length > 0
                    ? trimmedCode
                    : 'Unspecified validation error';

            return `${this.escapeHtml(displayLabel)}: ${this.escapeHtml(reasonSource)}`;
        });

        const truncated = failures.length > previewLimit;
        const suffix = truncated ? '<br/>…additional players omitted' : '';
        const html = `<br/><br/><strong>Rejected player(s):</strong><br/>${previewItems.join('<br/>')}${suffix}`;

        return { html, truncated };
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    // Print Methods
    printAlternates(): void {
        this.runTournamentReport({
            reportId: 1,
            actionLabel: 'Alternates',
            processingSignal: this.isPrintingAlternates,
            fallbackFileName: 'alternates.pdf',
            successMessage: 'Alternates report downloaded.',
            logContext: 'printAlternates'
        });
    }

    // Alternates Sort Handler
    onAlternatesSort(event: { key: string; direction: 'asc' | 'desc' }): void {
        this.alternatesSortConfig.set(event);
        this.alternatesCurrentPage.set(1); // Reset to first page when sorting
    }

    // Alternates Pagination Handler
    onAlternatesPageChange(event: PageChangeEvent): void {
        this.alternatesCurrentPage.set(event.page);
        if (event.pageSize !== this.alternatesPageSize()) {
            this.alternatesPageSize.set(event.pageSize);
        }
    }

    // Selection Methods
    selectFieldPlayer(playerId: string): void {
        if (this.selectedFieldPlayerId() === playerId) {
            this.selectedFieldPlayerId.set(null); // Deselect if clicking same player
        } else {
            this.selectedFieldPlayerId.set(playerId);
        }
    }

    selectAlternatePlayer(playerId: string): void {
        if (this.selectedAlternatePlayerId() === playerId) {
            this.selectedAlternatePlayerId.set(null); // Deselect if clicking same player
        } else {
            this.selectedAlternatePlayerId.set(playerId);
        }
    }

    isFieldPlayerSelected(playerId: string): boolean {
        return this.selectedFieldPlayerId() === playerId;
    }

    isAlternatePlayerSelected(playerId: string): boolean {
        return this.selectedAlternatePlayerId() === playerId;
    }

    printFieldList(): void {
        this.runTournamentReport({
            reportId: 9,
            actionLabel: 'Field List',
            processingSignal: this.isPrintingFieldList,
            fallbackFileName: 'field-list.pdf',
            successMessage: 'Field list downloaded.',
            logContext: 'printFieldList'
        });
    }

    private async runTournamentReport(options: {
        reportId: number;
        actionLabel: string;
        processingSignal: WritableSignal<boolean>;
        fallbackFileName: string;
        successMessage: string;
        logContext: string;
    }): Promise<void> {
        if (!this.tournamentId) {
            this.toastr.error('Please select a tournament before printing.', 'No Tournament Selected');
            return;
        }

        const tournamentName = (this.tournamentName() ?? '').trim();
        if (!tournamentName) {
            this.toastr.error('Tournament details are still loading. Please try again shortly.', 'Tournament Not Ready');
            return;
        }

        if (options.processingSignal()) {
            return;
        }

        options.processingSignal.set(true);

        try {
            await this.reportExecutionService.executeAndDownload({
                reportId: options.reportId,
                parameters: { Tournament: tournamentName },
                context: { tournamentId: this.tournamentId },
                fileNameFallback: options.fallbackFileName,
                successMessage: options.successMessage,
            });
        } catch (error) {
            this.loggingService.error('TournamentPlayersComponent', `Failed to execute ${options.logContext}`, error);
        } finally {
            options.processingSignal.set(false);
        }
    }
}
